/**
 * SASADM-0154 — the brand editor's Theme tab (saas-auth SASATH-0346, handoff
 * "go" §3/§4 and #962 §4.2).
 *
 * - The form state is the whole `input` from `GET …/theme`, edited in place and
 *   always sent back whole ("go" §4.1). `cornerStyle: "custom"` is never sent:
 *   while it is still custom the key is omitted (a missing container is kept).
 * - Every input change posts a debounced (~250 ms) preview; the response
 *   replaces `resolved`/`contrast`/`warnings`, and a stale response is ignored.
 * - The live preview renders only from `resolved` (no client-side colour maths).
 * - A contrast failure blocks Save theme only when it is new: no failure from
 *   the last GET/save has the same mode, pair and colours (SASATH-0346 N2).
 * - Google font stylesheets load only from `fonts.googleapis.com` (the
 *   CLAUDE.md CSP exception), deduplicated, and are removed on unmount.
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { apiAdmin } from '@/api/admin';
import { DEFAULT_RETRY_AFTER_SECONDS } from '@/api/client';
import { ApiError } from '@/api/errors';
import type {
  ContrastFailure,
  FontOption,
  ResolvedTheme,
  ThemeContrast,
  ThemeCornerStyle,
  ThemeFont,
  ThemeInput,
  ThemeMode,
  ThemeResponse,
  ThemeSaveInput,
  ThemeSeeds,
  ThemeWarning,
} from '@/api/types';
import { HexColorField } from '@/components/HexColorField';
import { useCopy } from '@/hooks/useCopy';
import { BrandThemePreview } from './BrandThemePreview';
import { ContrastFailureRow } from './ContrastFailureRow';
import { failureKey } from './contrastFailureKey';
import { OVERRIDE_GROUPS, STATUS_SEEDS } from './themeRoles';

export const PREVIEW_DEBOUNCE_MS = 250;
const CATEGORICAL_SLOTS = 8;
const GOOGLE_FONTS_ORIGIN = 'https://fonts.googleapis.com';
const SYSTEM_FONT_SUGGESTIONS = [
  'Georgia',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Verdana',
  'system-ui',
];
const SYSTEM_FAMILY_REGEX = /^[A-Za-z0-9 -]{1,100}$/;
const FONT_SLOTS = ['body', 'heading', 'mono'] as const;
type FontSlot = (typeof FONT_SLOTS)[number];

const BRAND_RAMP_ROLES = ['brandSoft', 'brand', 'brandHover', 'brandDeep'];
const ACCENT_RAMP_ROLES = ['accentSoft', 'accent', 'accentHover'];

/** The PUT/preview body: `cornerStyle` omitted while custom; trailing Auto categorical slots dropped. */
function toSaveInput(input: ThemeInput): ThemeSaveInput {
  const categorical = [...input.seeds.categorical];
  while (categorical.length > 0 && categorical[categorical.length - 1] == null) categorical.pop();
  const { cornerStyle, ...rest } = input;
  return {
    ...rest,
    seeds: { ...input.seeds, categorical },
    ...(cornerStyle !== 'custom' ? { cornerStyle } : {}),
  };
}

/** A Google font stylesheet URL, or null when it isn't on `fonts.googleapis.com` over https. */
function googleFontsUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin === GOOGLE_FONTS_ORIGIN ? url : null;
  } catch {
    return null;
  }
}

function pickerPreviewUrl(family: string): string {
  return `${GOOGLE_FONTS_ORIGIN}/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}&display=swap`;
}

/**
 * Keeps one `<link rel="stylesheet">` per URL in the document head (deduped,
 * including against links already present) and removes the links it added on
 * unmount. Only `fonts.googleapis.com` URLs reach this.
 */
function useStylesheetLinks(urls: string[]) {
  const added = useRef(new Map<string, HTMLLinkElement>());
  const key = [...new Set(urls)].sort().join('\n');
  useEffect(() => {
    for (const url of key ? key.split('\n') : []) {
      if (added.current.has(url)) continue;
      const existing = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).some(
        (l) => l.getAttribute('href') === url,
      );
      if (existing) continue;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
      added.current.set(url, link);
    }
  }, [key]);
  useEffect(() => {
    const links = added.current;
    return () => {
      links.forEach((l) => l.remove());
      links.clear();
    };
  }, []);
}

function contrastFailuresOf(err: ApiError): ContrastFailure[] {
  const raw = err.details.raw as { error?: { failures?: ContrastFailure[] } } | undefined;
  return Array.isArray(raw?.error?.failures) ? raw.error.failures : [];
}

function RampStrip({
  resolved,
  roles,
  label,
}: {
  resolved: ResolvedTheme;
  roles: string[];
  label: string;
}) {
  return (
    <Stack
      direction="row"
      role="list"
      aria-label={label}
      sx={{ mt: 1, ml: 6, borderRadius: 1, overflow: 'hidden', width: 'fit-content' }}
    >
      {roles.map((role) => {
        const color = resolved.modes.light[role];
        return color ? (
          <Box
            key={role}
            role="listitem"
            title={`${role} ${color}`}
            aria-label={`${role} ${color}`}
            sx={{ width: 32, height: 16 }}
            style={{ backgroundColor: color }}
          />
        ) : null;
      })}
    </Stack>
  );
}

interface BrandThemePanelProps {
  brandId: number;
  /** Tenants on this brand (the loader's `tenantsUsingTotal`). */
  tenantsUsingTotal: number;
  /** Bumped by the page after a legacy save or image upload — the etag is then stale. */
  staleToken: number;
  /** Whether the Theme tab is showing. */
  active: boolean;
  /** Called after a 200 theme save so the page refetches the brand. */
  onSaved: () => void;
  name: string;
}

export function BrandThemePanel({
  brandId,
  tenantsUsingTotal,
  staleToken,
  active,
  onSaved,
  name,
}: BrandThemePanelProps) {
  const { t } = useCopy();
  const [input, setInput] = useState<ThemeInput | null>(null);
  const [serverInput, setServerInput] = useState<ThemeInput | null>(null);
  const [resolved, setResolved] = useState<ResolvedTheme | null>(null);
  const [contrast, setContrast] = useState<ThemeContrast>({ ok: true, failures: [] });
  const [warnings, setWarnings] = useState<ThemeWarning[]>([]);
  const [etag, setEtag] = useState<string | null>(null);
  const [baselineKeys, setBaselineKeys] = useState<ReadonlySet<string>>(new Set());
  const [fontOptions, setFontOptions] = useState<FontOption[]>([]);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [invalidFields, setInvalidFields] = useState<ReadonlySet<string>>(new Set());
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [pickerFontsWanted, setPickerFontsWanted] = useState(false);
  const [overrideGroup, setOverrideGroup] = useState(OVERRIDE_GROUPS[0]!.id);
  // The staleToken the last GET covered; -1 until the first load.
  const loadedToken = useRef(-1);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewSeq = useRef(0);

  const dirty = input !== null && JSON.stringify(input) !== JSON.stringify(serverInput);

  function applyServer(res: ThemeResponse, replaceForm: boolean) {
    setServerInput(res.input);
    setEtag(res.etag);
    setBaselineKeys(new Set(res.contrast.failures.map(failureKey)));
    if (replaceForm) {
      previewSeq.current += 1; // drop any in-flight preview of the old form
      if (previewTimer.current) clearTimeout(previewTimer.current);
      setInput(res.input);
      setResolved(res.resolved);
      setContrast(res.contrast);
      setWarnings(res.warnings);
      setPreviewError(null);
      setHighlighted(null);
    }
  }

  /**
   * GET the theme. `replaceForm` false keeps unsaved edits (only the etag and
   * the contrast baseline refresh) — used when the page marked the etag stale.
   * It adopts the new etag only when the theme input itself is unchanged (a
   * legacy save or upload never changes it); if someone else changed the theme,
   * the old etag is kept so the next save fails closed with 409 THEME_CHANGED
   * instead of silently overwriting their change.
   */
  async function load(replaceForm: boolean): Promise<string | null> {
    const token = staleToken;
    try {
      const res = await apiAdmin.brandTheme.get(brandId);
      loadedToken.current = token;
      setLoadError(null);
      if (!replaceForm && JSON.stringify(res.input) !== JSON.stringify(serverInput)) return etag;
      applyServer(res, replaceForm);
      return res.etag;
    } catch (err) {
      if (err instanceof ApiError) setLoadError(err);
      return null;
    }
  }

  // Load on first show, and re-GET when the tab becomes visible after the page
  // marked the theme stale (a legacy save or an upload changed the etag).
  useEffect(() => {
    if (!active || loadedToken.current === staleToken) return;
    void load(loadedToken.current === -1 || !dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on visibility/staleness only
  }, [active, staleToken]);

  useEffect(() => {
    let cancelled = false;
    apiAdmin.brandTheme
      .fonts()
      .then((list) => {
        if (!cancelled) setFontOptions(list);
      })
      .catch(() => {
        /* the Google picker then offers only the current family */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () => () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    },
    [],
  );

  const fontUrls: string[] = [];
  if (resolved) {
    for (const slot of FONT_SLOTS) {
      const url = googleFontsUrl(resolved.fonts?.[slot]?.cssUrl);
      if (url) fontUrls.push(url);
    }
  }
  if (pickerFontsWanted) fontUrls.push(...fontOptions.map((f) => pickerPreviewUrl(f.family)));
  useStylesheetLinks(fontUrls);

  async function runPreview(next: ThemeInput, retried = false) {
    const seq = ++previewSeq.current;
    try {
      const res = await apiAdmin.brandTheme.preview(brandId, toSaveInput(next));
      if (seq !== previewSeq.current) return;
      setResolved(res.resolved);
      setContrast(res.contrast);
      setWarnings(res.warnings);
      setPreviewError(null);
    } catch (err) {
      if (seq !== previewSeq.current || !(err instanceof ApiError)) return;
      if (err.status === 429 && !retried) {
        const wait = (err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS) * 1000;
        previewTimer.current = setTimeout(() => {
          if (seq === previewSeq.current) void runPreview(next, true);
        }, wait);
        return;
      }
      setPreviewError(err);
    }
  }

  function change(updater: (prev: ThemeInput) => ThemeInput) {
    if (!input) return;
    const next = updater(input);
    setInput(next);
    setSaveError(null);
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => void runPreview(next), PREVIEW_DEBOUNCE_MS);
  }

  function setSeed<K extends keyof ThemeSeeds>(key: K, value: ThemeSeeds[K]) {
    change((prev) => ({ ...prev, seeds: { ...prev.seeds, [key]: value } }));
  }

  function setCategorical(index: number, value: string | null) {
    change((prev) => {
      const slots = Array.from(
        { length: CATEGORICAL_SLOTS },
        (_, i) => prev.seeds.categorical[i] ?? null,
      );
      slots[index] = value;
      return { ...prev, seeds: { ...prev.seeds, categorical: slots } };
    });
  }

  function setOverride(mode: ThemeMode, role: string, value: string | null) {
    change((prev) => {
      const map = { ...prev.overrides[mode] };
      if (value) map[role] = value;
      else delete map[role];
      return { ...prev, overrides: { ...prev.overrides, [mode]: map } };
    });
  }

  function setFont(slot: FontSlot, font: ThemeFont) {
    change((prev) => ({ ...prev, fonts: { ...prev.fonts, [slot]: font } }));
  }

  function validity(fieldId: string) {
    return (valid: boolean) =>
      setInvalidFields((prev) => {
        if (prev.has(fieldId) !== valid) return prev;
        const next = new Set(prev);
        if (valid) next.delete(fieldId);
        else next.add(fieldId);
        return next;
      });
  }

  const blocking = contrast.failures.filter((f) => !baselineKeys.has(failureKey(f)));
  const nonBlocking = contrast.failures.filter((f) => baselineKeys.has(failureKey(f)));
  const invalidSystemFonts = input
    ? FONT_SLOTS.filter(
        (s) =>
          input.fonts[s].source === 'system' && !SYSTEM_FAMILY_REGEX.test(input.fonts[s].family),
      )
    : [];
  const saveDisabled =
    !input ||
    !etag ||
    saving ||
    blocking.length > 0 ||
    invalidFields.size > 0 ||
    invalidSystemFonts.length > 0;

  async function handleSave() {
    if (!input) return;
    setSaving(true);
    setSaveError(null);
    try {
      let currentEtag = etag;
      if (loadedToken.current !== staleToken) currentEtag = await load(false);
      if (!currentEtag) return;
      const res = await apiAdmin.brandTheme.save(brandId, toSaveInput(input), currentEtag);
      applyServer(res, true);
      onSaved();
    } catch (err) {
      if (!(err instanceof ApiError)) return;
      if (err.code === 'THEME_CONTRAST_FAILED' || err.status === 422) {
        // Render the server's failures exactly like live ones; nothing was saved.
        const failures = contrastFailuresOf(err);
        setContrast((prev) => {
          const seen = new Set(prev.failures.map(failureKey));
          return {
            ok: false,
            failures: [...prev.failures, ...failures.filter((f) => !seen.has(failureKey(f)))],
          };
        });
      }
      setSaveError(err);
    } finally {
      setSaving(false);
    }
  }

  function saveErrorMessage(err: ApiError): string {
    if (err.code === 'THEME_CHANGED' || err.status === 409) {
      return t('brands.theme.save.changed', 'This theme changed elsewhere — reload');
    }
    if (err.code === 'THEME_CONTRAST_FAILED' || err.status === 422) {
      return t(
        'brands.theme.save.contrastFailed',
        'A changed colour fails WCAG AA contrast. Nothing was saved.',
      );
    }
    if (err.status === 429) {
      return t('brands.theme.save.rateLimited', 'Too many saves — try again in {{n}} s', {
        n: err.details.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS,
      });
    }
    return (
      err.details.message ??
      t(`errors.codes.${err.code}`, t('errors.generic', 'Something went wrong.'))
    );
  }

  function renderFailure(f: ContrastFailure, isBlocking: boolean) {
    const key = failureKey(f);
    const on = highlighted === key;
    return (
      <ContrastFailureRow
        key={key}
        failure={f}
        highlighted={on}
        onToggle={() => setHighlighted(on ? null : key)}
        status={isBlocking ? 'blocking' : 'existing'}
      />
    );
  }

  if (loadError && !input) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void load(true)}>
            {t('brands.theme.reload', 'Reload')}
          </Button>
        }
      >
        {t('brands.theme.loadFailed', "Couldn't load the theme.")}
      </Alert>
    );
  }
  if (!input || !resolved) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label={t('brands.theme.loading', 'Loading theme')} />
      </Box>
    );
  }

  const light = resolved.modes.light ?? {};
  const cornerStyle: ThemeCornerStyle = input.cornerStyle;
  const googleByCategory = new Map<string, FontOption[]>();
  for (const f of fontOptions) {
    const list = googleByCategory.get(f.category) ?? [];
    list.push(f);
    googleByCategory.set(f.category, list);
  }

  // A collapsed Advanced sub-section; its fields mount only while it is open.
  function advancedSection(id: string, title: string, content: ReactNode): ReactNode {
    return (
      <Accordion
        variant="outlined"
        disableGutters
        slotProps={{ transition: { unmountOnExit: true } }}
        data-testid={`theme-advanced-${id}`}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>{content}</AccordionDetails>
      </Accordion>
    );
  }

  function fontPicker(slot: FontSlot): ReactNode {
    const font = input!.fonts[slot];
    const label = t(`brands.theme.fonts.${slot}`, slot);
    const known = fontOptions.some((f) => f.family === font.family);
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ minWidth: 72 }}>
            {label}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={font.source}
            aria-label={t('brands.theme.fonts.sourceLabel', '{{slot}} font source', {
              slot: label,
            })}
            onChange={(_e, source: ThemeFont['source'] | null) => {
              if (!source || source === font.source) return;
              const family =
                source === 'google'
                  ? (fontOptions.find((f) => f.family === font.family)?.family ??
                    fontOptions[0]?.family ??
                    font.family)
                  : font.family;
              setFont(slot, { family, source });
            }}
          >
            <ToggleButton value="google">{t('brands.theme.fonts.google', 'Google')}</ToggleButton>
            <ToggleButton value="system">{t('brands.theme.fonts.system', 'System')}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {font.source === 'google' ? (
          <TextField
            select
            fullWidth
            size="small"
            label={t('brands.theme.fonts.familyLabel', '{{slot}} font', { slot: label })}
            value={font.family}
            onChange={(e) => setFont(slot, { family: e.target.value, source: 'google' })}
            slotProps={{ select: { onOpen: () => setPickerFontsWanted(true) } }}
          >
            {!known ? <MenuItem value={font.family}>{font.family}</MenuItem> : null}
            {[...googleByCategory.entries()].flatMap(([category, list]) => [
              <ListSubheader key={`h-${category}`}>{category}</ListSubheader>,
              ...list.map((f) => (
                <MenuItem
                  key={f.family}
                  value={f.family}
                  sx={{
                    fontFamily: `'${f.family}', ${category === 'display' ? 'sans-serif' : category}`,
                  }}
                >
                  {f.family}
                </MenuItem>
              )),
            ])}
          </TextField>
        ) : (
          <Autocomplete
            freeSolo
            options={SYSTEM_FONT_SUGGESTIONS}
            inputValue={font.family}
            onInputChange={(_e, value) => setFont(slot, { family: value, source: 'system' })}
            renderInput={(params) => {
              const invalid = invalidSystemFonts.includes(slot);
              return (
                <TextField
                  {...params}
                  size="small"
                  label={t('brands.theme.fonts.familyLabel', '{{slot}} font', { slot: label })}
                  error={invalid}
                  helperText={
                    invalid
                      ? t(
                          'brands.theme.fonts.systemInvalid',
                          'Up to 100 letters, digits, spaces and hyphens.',
                        )
                      : ''
                  }
                />
              );
            }}
          />
        )}
      </Stack>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, lg: 5 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={3}>
            {tenantsUsingTotal > 0 ? (
              <Alert severity="info" data-testid="theme-used-by">
                {t(
                  'brands.theme.usedBy',
                  'Used by {{n}} tenants — theme changes apply to all of them.',
                  {
                    n: tenantsUsingTotal,
                  },
                )}
              </Alert>
            ) : null}

            <Box>
              <Typography variant="h3" gutterBottom>
                {t('brands.theme.brandColours', 'Brand colours')}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <HexColorField
                    label={t('brands.theme.seeds.primary', 'Primary')}
                    value={input.seeds.primary}
                    onChange={(hex) => setSeed('primary', hex)}
                    onValidityChange={validity('seeds.primary')}
                  />
                  <RampStrip
                    resolved={resolved}
                    roles={BRAND_RAMP_ROLES}
                    label={t('brands.theme.brandRamp', 'Brand shades')}
                  />
                </Box>
                <Box>
                  <HexColorField
                    auto
                    label={t('brands.theme.seeds.accent', 'Accent')}
                    value={input.seeds.accent}
                    resolvedValue={light.accent}
                    onChange={(hex) => setSeed('accent', hex)}
                    onReset={() => setSeed('accent', null)}
                    onValidityChange={validity('seeds.accent')}
                  />
                  <RampStrip
                    resolved={resolved}
                    roles={ACCENT_RAMP_ROLES}
                    label={t('brands.theme.accentRamp', 'Accent shades')}
                  />
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography variant="h3" gutterBottom>
                {t('brands.theme.surfaces', 'Surfaces')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={input.seeds.surfaceStyle}
                aria-label={t('brands.theme.surfaces', 'Surfaces')}
                onChange={(_e, v: ThemeSeeds['surfaceStyle'] | null) => {
                  if (v) setSeed('surfaceStyle', v);
                }}
              >
                <ToggleButton value="light-tinted">
                  {t('brands.theme.surfaceTinted', 'Tinted')}
                </ToggleButton>
                <ToggleButton value="pure">{t('brands.theme.surfacePure', 'Pure')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="h3" gutterBottom>
                {t('brands.theme.shape', 'Shape')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cornerStyle}
                aria-label={t('brands.theme.shape', 'Shape')}
                onChange={(_e, v: ThemeCornerStyle | null) => {
                  if (v && v !== 'custom') change((prev) => ({ ...prev, cornerStyle: v }));
                }}
              >
                <ToggleButton value="sharp">{t('brands.theme.cornerSharp', 'Sharp')}</ToggleButton>
                <ToggleButton value="rounded">
                  {t('brands.theme.cornerRounded', 'Rounded')}
                </ToggleButton>
                <ToggleButton value="pill">{t('brands.theme.cornerPill', 'Pill')}</ToggleButton>
                {cornerStyle === 'custom' ? (
                  <ToggleButton value="custom" disabled>
                    {t('brands.theme.cornerCustom', 'Custom radius — pick a style to replace it')}
                  </ToggleButton>
                ) : null}
              </ToggleButtonGroup>
              <Stack direction="row" spacing={2} sx={{ mt: 1.5, alignItems: 'center' }}>
                <Box
                  component="span"
                  data-testid="corner-sample-button"
                  sx={{ px: 2, py: 0.75, fontSize: 13 }}
                  style={{
                    backgroundColor: light.brand,
                    color: light.brandInk,
                    borderRadius: resolved.radius.control,
                  }}
                >
                  {t('brands.theme.sampleButton', 'Button')}
                </Box>
                <Box
                  data-testid="corner-sample-card"
                  sx={{ width: 96, height: 48, border: 1 }}
                  style={{
                    backgroundColor: light.surface,
                    borderColor: light.line,
                    borderRadius: resolved.radius.card,
                  }}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="h3" gutterBottom>
                {t('brands.theme.typography', 'Typography')}
              </Typography>
              <Stack spacing={2}>
                {FONT_SLOTS.map((slot) => (
                  <Box key={slot}>{fontPicker(slot)}</Box>
                ))}
              </Stack>
            </Box>

            <Accordion disableGutters slotProps={{ transition: { unmountOnExit: true } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t('brands.theme.advanced', 'Advanced')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {advancedSection(
                    'seeds',
                    t('brands.theme.seedsSection', 'Neutral, status & money'),
                    <Stack spacing={2}>
                      <HexColorField
                        auto
                        disabled={input.seeds.surfaceStyle === 'pure'}
                        label={t('brands.theme.seeds.neutral', 'Neutral')}
                        value={input.seeds.neutral}
                        onChange={(hex) => setSeed('neutral', hex)}
                        onReset={() => setSeed('neutral', null)}
                        onValidityChange={validity('seeds.neutral')}
                      />
                      {input.seeds.surfaceStyle === 'pure' ? (
                        <Typography variant="caption" color="text.secondary">
                          {t(
                            'brands.theme.neutralPure',
                            'Pure surfaces are grey — the neutral seed is not used.',
                          )}
                        </Typography>
                      ) : null}
                      <Divider />
                      <Typography variant="subtitle2">
                        {t('brands.theme.status', 'Status')}
                      </Typography>
                      {STATUS_SEEDS.map((s) => (
                        <HexColorField
                          key={s}
                          auto
                          label={t(`brands.theme.seeds.${s}`, s)}
                          value={input.seeds[s]}
                          resolvedValue={light[`${s}Fg`]}
                          onChange={(hex) => setSeed(s, hex)}
                          onReset={() => setSeed(s, null)}
                          onValidityChange={validity(`seeds.${s}`)}
                        />
                      ))}
                      <Divider />
                      <Typography variant="subtitle2">
                        {t('brands.theme.money', 'Money')}
                      </Typography>
                      {(['moneyPositive', 'moneyNegative'] as const).map((m) => (
                        <HexColorField
                          key={m}
                          auto
                          label={t(`brands.theme.seeds.${m}`, m)}
                          value={input.seeds[m]}
                          resolvedValue={light[m]}
                          onChange={(hex) => setSeed(m, hex)}
                          onReset={() => setSeed(m, null)}
                          onValidityChange={validity(`seeds.${m}`)}
                        />
                      ))}
                    </Stack>,
                  )}
                  {advancedSection(
                    'categorical',
                    t('brands.theme.categorical', 'Categorical'),
                    <Stack spacing={2}>
                      {Array.from({ length: CATEGORICAL_SLOTS }, (_, i) => (
                        <HexColorField
                          key={i}
                          auto
                          label={t('brands.theme.categoricalSlot', 'Category {{n}}', { n: i + 1 })}
                          value={input.seeds.categorical[i] ?? null}
                          resolvedValue={resolved.categorical?.light?.[i]?.fill}
                          onChange={(hex) => setCategorical(i, hex)}
                          onReset={() => setCategorical(i, null)}
                          onValidityChange={validity(`seeds.categorical.${i}`)}
                        />
                      ))}
                    </Stack>,
                  )}
                  {advancedSection(
                    'overrides',
                    t('brands.theme.overrides', 'Overrides'),
                    <Stack spacing={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          'brands.theme.overridesHint',
                          'Each cell shows the resolved value. Setting one pins it; Reset clears it.',
                        )}
                      </Typography>
                      <TextField
                        select
                        size="small"
                        label={t('brands.theme.overrideGroup', 'Role group')}
                        value={overrideGroup}
                        onChange={(e) => setOverrideGroup(e.target.value)}
                        slotProps={{ select: { native: true } }}
                        sx={{ maxWidth: 240 }}
                      >
                        {OVERRIDE_GROUPS.map((g) => (
                          <option key={g.id} value={g.id}>
                            {t(`brands.theme.overrideGroups.${g.id}`, g.id)}
                          </option>
                        ))}
                      </TextField>
                      <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label={t('brands.theme.overrides', 'Overrides')}>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('brands.theme.role', 'Role')}</TableCell>
                              <TableCell>{t('brands.theme.preview.light', 'Light')}</TableCell>
                              <TableCell>{t('brands.theme.preview.dark', 'Dark')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(OVERRIDE_GROUPS.find((g) => g.id === overrideGroup)?.roles ?? []).map(
                              (role) => (
                                <TableRow key={role} data-testid={`override-row-${role}`}>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>{role}</TableCell>
                                  {(['light', 'dark'] as const).map((mode) => (
                                    <TableCell key={mode} sx={{ minWidth: 260 }}>
                                      <HexColorField
                                        auto
                                        label={t('brands.theme.overrideCell', '{{mode}} {{role}}', {
                                          mode:
                                            mode === 'light'
                                              ? t('brands.theme.preview.light', 'Light')
                                              : t('brands.theme.preview.dark', 'Dark'),
                                          role,
                                        })}
                                        value={input.overrides[mode][role] ?? null}
                                        resolvedValue={resolved.modes[mode]?.[role]}
                                        onChange={(hex) => setOverride(mode, role, hex)}
                                        onReset={() => setOverride(mode, role, null)}
                                        onValidityChange={validity(`overrides.${mode}.${role}`)}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ),
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Stack>,
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {previewError ? (
              <Alert severity="error" data-testid="theme-preview-error">
                {previewError.status === 429
                  ? t(
                      'brands.theme.previewRateLimited',
                      'Too many preview requests — edit again in a moment.',
                    )
                  : (previewError.details.message ??
                    t(
                      `errors.codes.${previewError.code}`,
                      t('errors.generic', 'Something went wrong.'),
                    ))}
              </Alert>
            ) : null}

            <Box data-testid="theme-contrast">
              <Typography variant="h3" gutterBottom>
                {t('brands.theme.contrast.title', 'Contrast')}
              </Typography>
              {contrast.failures.length === 0 ? (
                <Alert severity="success">
                  {t('brands.theme.contrast.ok', 'All text meets WCAG AA')}
                </Alert>
              ) : (
                <Box component="ul" sx={{ p: 0, m: 0 }}>
                  {blocking.map((f) => renderFailure(f, true))}
                  {nonBlocking.map((f) => renderFailure(f, false))}
                </Box>
              )}
              {warnings.map((w) => (
                <Alert key={w.code} severity="warning" sx={{ mt: 1 }}>
                  {w.message}
                </Alert>
              ))}
            </Box>

            {saveError ? (
              <Alert
                severity="error"
                data-testid="theme-save-error"
                action={
                  saveError.code === 'THEME_CHANGED' || saveError.status === 409 ? (
                    <Button color="inherit" size="small" onClick={() => void load(true)}>
                      {t('brands.theme.reload', 'Reload')}
                    </Button>
                  ) : undefined
                }
              >
                {saveErrorMessage(saveError)}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={() => void handleSave()}
                disabled={saveDisabled}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {t('brands.theme.save.button', 'Save theme')}
              </Button>
              {dirty ? (
                <Typography variant="caption" color="text.secondary">
                  {t('brands.theme.unsaved', 'Unsaved theme changes')}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      <Grid size={{ xs: 12, lg: 7 }}>
        <BrandThemePreview resolved={resolved} name={name} />
      </Grid>
    </Grid>
  );
}
